import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import toast from "react-hot-toast";
import TelegramOidcLoginButton from "../components/TelegramOidcLoginButton";

const BACKEND_URL = process.env.REACT_APP_BASE_URL;

const GoogleIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.803 32.658 29.303 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.962 3.038l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 12 24 12c3.059 0 5.842 1.154 7.962 3.038l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.281 0-9.767-3.321-11.29-7.946l-6.521 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.723 2.044-2.043 3.776-3.784 4.965l.002-.001 6.19 5.238C36.27 39.556 44 34 44 24c0-1.341-.138-2.651-.389-3.917z"
    />
  </svg>
);

const AuthPage: React.FC = () => {
  const [tab, setTab] = useState<"login" | "register">("login");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    window.location.href = `${BACKEND_URL}/auth/google`;
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tab === "register" && form.password !== form.confirm_password) {
      toast.error(t("passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
      } else {
        await register(
          form.name,
          form.email,
          form.password,
          form.confirm_password,
        );
      }
      navigate("/dashboard");
    } catch (err: any) {
      const data = err?.response?.data;
      let msg = t("errorOccurred");
      if (typeof data?.message === "string") msg = data.message;
      else if (typeof data?.message?.message === "string")
        msg = data.message.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inp = (
    icon: any,
    type: string,
    placeholder: string,
    key: string,
    extra?: React.ReactNode,
  ) => {
    const Icon = icon;
    return (
      <div style={{ position: "relative" }}>
        <Icon
          size={14}
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#475569",
          }}
        />
        <input
          type={type}
          placeholder={placeholder}
          value={(form as any)[key]}
          onChange={set(key)}
          required
          style={{
            width: "100%",
            background: "#0D1526",
            border: "1px solid #1E2D45",
            borderRadius: 10,
            padding: "11px 36px 11px 36px",
            color: "#E2E8F0",
            fontSize: 14,
            fontFamily: "inherit",
            outline: "none",
            transition: "border .2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#3B82F6")}
          onBlur={(e) => (e.target.style.borderColor = "#1E2D45")}
        />
        {extra}
      </div>
    );
  };

  const eyeBtn = (show: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      style={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        background: "transparent",
        border: "none",
        color: "#475569",
        cursor: "pointer",
        display: "flex",
      }}>
      {show ?
        <EyeOff size={14} />
      : <Eye size={14} />}
    </button>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0E1A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        overflow: "hidden",
      }}>
      {/* BG glow */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 400,
          height: 250,
          background:
            "radial-gradient(ellipse, rgba(59,130,246,.16) 0%, rgba(59,130,246,.05) 45%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Lang switcher */}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          display: "flex",
          gap: 4,
        }}>
        {(["oz", "uz", "ru"] as const).map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            style={{
              padding: "5px 9px",
              borderRadius: 8,
              border: "1px solid",
              borderColor: lang === l ? "#3B82F6" : "#1E2D45",
              background: lang === l ? "rgba(59,130,246,.15)" : "transparent",
              color: lang === l ? "#3B82F6" : "#64748B",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}>
            {l === "oz" ?
              "O'Z"
            : l === "uz" ?
              "Ўз"
            : "RU"}
          </button>
        ))}
      </div>

      <div style={{ width: "100%", maxWidth: 380 }} className="fade-up">
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 52, height: 52, margin: "0 auto 12px" }}>
            <img
              src="/logo.png"
              alt="AvtoMind"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          <h1
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 20,
              marginBottom: 4,
            }}>
            {tab === "login" ? "AvtoMind" : t("register")}
          </h1>
          <p style={{ color: "#64748B", fontSize: 13 }}>
            {tab === "login" ? t("authSubtitle") : t("authRegisterSubtitle")}
          </p>
        </div>

        <div
          style={{
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 16,
            padding: 24,
          }}>
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {tab === "register" && (
              <div>
                <label
                  style={{
                    display: "block",
                    color: "#64748B",
                    fontSize: 11,
                    fontWeight: 700,
                    marginBottom: 6,
                  }}>
                  {t("fullName")}
                </label>
                {inp(User, "text", t("namePlaceholder"), "name")}
              </div>
            )}

            <div>
              <label
                style={{
                  display: "block",
                  color: "#64748B",
                  fontSize: 11,
                  fontWeight: 700,
                  marginBottom: 6,
                }}>
                {tab === "login" ? t("emailOrUsername") : t("email")}
              </label>
              {inp(Mail, "email", "your@email.com", "email")}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  color: "#64748B",
                  fontSize: 11,
                  fontWeight: 700,
                  marginBottom: 6,
                }}>
                {t("password")}
              </label>
              {inp(
                Lock,
                showPass ? "text" : "password",
                "••••••••",
                "password",
                eyeBtn(showPass, () => setShowPass(!showPass)),
              )}
            </div>

            {tab === "register" && (
              <div>
                <label
                  style={{
                    display: "block",
                    color: "#64748B",
                    fontSize: 11,
                    fontWeight: 700,
                    marginBottom: 6,
                  }}>
                  {t("confirmPassword")}
                </label>
                {inp(
                  Lock,
                  showConf ? "text" : "password",
                  "••••••••",
                  "confirm_password",
                  eyeBtn(showConf, () => setShowConf(!showConf)),
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "13px",
                borderRadius: 10,
                border: "none",
                background: "#3B82F6",
                color: "#fff",
                fontWeight: 800,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.8 : 1,
                marginTop: 4,
                transition: "all .2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => {
                if (!loading)
                  (e.currentTarget as HTMLElement).style.background = "#2563EB";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = "#3B82F6";
              }}>
              {loading ?
                <>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "2.5px solid rgba(255,255,255,.3)",
                      borderTopColor: "#fff",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  {t("loadingText")}
                </>
              : tab === "login" ?
                t("login")
              : t("register")}
            </button>
          </form>

          {/* Divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 12,
              marginBottom: 12,
            }}>
            <div style={{ height: 1, flex: 1, background: "#1E2D45" }} />
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 700 }}>
              {t("or")}
            </span>
            <div style={{ height: 1, flex: 1, background: "#1E2D45" }} />
          </div>

          {/* Google + Telegram — yonma-yon, ixcham */}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={handleGoogleLogin}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: 10,
                border: "1px solid #1E2D45",
                background: "#0D1526",
                color: "#E2E8F0",
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                transition: "all .2s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "#3B82F6";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "#1E2D45";
              }}>
              <GoogleIcon />
              {t("googleLogin")}
            </button>

            <TelegramOidcLoginButton />
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #1E2D45",
              textAlign: "center",
            }}>
            {tab === "login" ?
              <p style={{ fontSize: 13, color: "#64748B" }}>
                {t("noAccount")}{" "}
                <button
                  type="button"
                  onClick={() => setTab("register")}
                  style={{
                    color: "#3B82F6",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                  }}>
                  {t("register")}
                </button>
              </p>
            : <p style={{ fontSize: 13, color: "#64748B" }}>
                {t("hasAccount")}{" "}
                <button
                  type="button"
                  onClick={() => setTab("login")}
                  style={{
                    color: "#3B82F6",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                  }}>
                  {t("login")}
                </button>
              </p>
            }
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthPage;
